import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ClockContainer, FocusInput, FocusInputContainer, FocusText, FocusCheckbox, FocusListItem, FocusList, ResizerHandle } from '../../styles/Dashboard.styles';
import { DeleteButton } from '../../styles/ProjectChecklistModal.styles';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import ClockSettingsModal from './ClockSettingsModal';
import MenuDotsIcon from '../icons/MenuDotsIcon';

function DashboardClock({ user, globalTodos, handleGlobalTodoOperations }) {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [focusListWidth, setFocusListWidth] = useLocalStorage('focusListWidth', 480);
    const [isResizing, setIsResizing] = useState(false);
    const initialMouseX = useRef(0);
    const initialWidth = useRef(0);
    const [greeting, setGreeting] = useState('');
    const [newFocusItem, setNewFocusItem] = useState('');
    const [editingItemId, setEditingItemId] = useState(null);
    const [editingItemText, setEditingItemText] = useState('');
    const [editingItemDueDate, setEditingItemDueDate] = useState('');
    const [visibleDueDateItemId, setVisibleDueDateItemId] = useState(null);
    const [isSettingsModalOpen, setSettingsModalOpen] = useState(false);
    const [clockSettings, setClockSettings] = useLocalStorage('clockSettings', {
        hour12: false,
        clockSize: 4,
    });

    const focusedItems = globalTodos.filter(todo => todo.focused);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    const handleToggleFocus = (item) => {
        const itemIndex = globalTodos.findIndex(todo => todo.id === item.id);
        if (itemIndex > -1) {
            handleGlobalTodoOperations.toggleFocus(itemIndex);
        }
    };

    const handleAddFocusItem = () => {
        if (newFocusItem.trim() === '') return;
        handleGlobalTodoOperations.add(newFocusItem, { focused: true, addToEnd: true });
        setNewFocusItem('');
    };

    const handleUpdateFocusItem = (id, newText, newDueDate = null) => {
        const itemIndex = globalTodos.findIndex(item => item.id === id);
        if (itemIndex > -1) {
            handleGlobalTodoOperations.update(itemIndex, { text: newText.trim(), dueDate: newDueDate !== undefined ? (newDueDate === '' ? null : newDueDate) : globalTodos[itemIndex].dueDate });
        }
        setEditingItemId(null); // Close editing mode
    };

    const getGreeting = () => {
        const hour = currentTime.getHours();
        const day = currentTime.getDay(); // 0 (Sunday) to 6 (Saturday)
        const name = user.displayName;

        const mondayGreetings = [
            `Happy Monday, ${name}! Kick off the week.`,
            `Monday, ${name}! Let's get productive.`,
            `Hello ${name}! Time to grind.`
        ];

        const fridayGreetings = [
            `Happy Friday, ${name}! Time to wind down.`,
            `TGIF, ${name}! Enjoy the weekend.`,
            `Friday, ${name}! You made it!`
        ];

        const morningGreetings = [
            `Rise and shine, ${name}!`,
            `Good morning, ${name}!`,
            `Top of the morning, ${name}!`
        ];

        const lunchTimeGreetings = [
            `Lunch time, ${name}!`,
            `Time for a break, ${name}!`,
            `Enjoy your lunch, ${name}!`
        ];

        const afternoonGreetings = [
            `Afternoon, ${name}! Keep it up.`,
            `Good afternoon, ${name}!`,
            `Keep pushing, ${name}!`
        ];

        const eveningGreetings = [
            `Evening, ${name}! Almost there.`,
	`Almost time to relax, ${name}!`,
            `One last push, ${name}!`
        ];

        const nightGreetings = [
            `Evening, ${name}! Time to relax.`,
            `Time to chill, ${name}!`,
            `Relax and unwind, ${name}!`
        ];

        const goodNightGreetings = [
            `Good night, ${name}!`,
            `Sweet dreams, ${name}!`,
            `Have a good rest, ${name}!`
        ];

        const getRandomGreeting = (greetings) => {
            return greetings[Math.floor(Math.random() * greetings.length)];
        };

        if (day === 1) { // Monday
            return getRandomGreeting(mondayGreetings);
        } else if (day === 5) { // Friday
            return getRandomGreeting(fridayGreetings);
        } else if (hour >= 5 && hour < 9) { // 5 AM - 9 AM
            return getRandomGreeting(morningGreetings);
        } else if (hour >= 9 && hour < 12) { // 9 AM - 12 PM
            return `Good morning, ${name}!`;
        } else if (hour >= 12 && hour < 14) { // 12 PM - 2 PM
            return getRandomGreeting(lunchTimeGreetings);
        } else if (hour >= 14 && hour < 17) { // 2 PM - 5 PM
            return getRandomGreeting(afternoonGreetings);
        } else if (hour >= 17 && hour < 20) { // 5 PM - 8 PM
            return getRandomGreeting(eveningGreetings);
        } else if (hour >= 20 && hour < 22) { // 8 PM - 10 PM
	return getRandomGreeting(nightGreetings);
        } else {
            return getRandomGreeting(goodNightGreetings);
        }
    };

    useEffect(() => {
        const newGreeting = getGreeting();
        setGreeting(newGreeting);
    }, []);

    useEffect(() => {
        const greetingIntervalId = setInterval(() => {
            const newGreeting = getGreeting();
            setGreeting(newGreeting);
        }, 600000); // 10 minutes in milliseconds

        return () => clearInterval(greetingIntervalId);
    }, [user]);

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }
        handleGlobalTodoOperations.reorder(result, focusedItems);
    };

    const handleMouseDown = (e) => {
        setIsResizing(true);
        initialMouseX.current = e.clientX;
        initialWidth.current = focusListWidth;
    };

    const handleMouseMove = useCallback((e) => {
        if (!isResizing) return;
        const deltaX = e.clientX - initialMouseX.current;
        const newWidth = Math.max(300, initialWidth.current + deltaX); // Min width of 300px
        setFocusListWidth(newWidth);
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    return (
        <ClockContainer>
            <div style={{ marginTop: '18vh', marginBottom: '18vh', textAlign: 'center' }}>
                <div className="clock-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
                    <div style={{ fontSize: `${clockSettings.clockSize}rem`, fontWeight: 'bold' }}>
                        {currentTime.toLocaleTimeString([], { hour12: clockSettings.hour12, hour: 'numeric', minute: '2-digit' })}
                    </div>
                    <button className="settings-button" onClick={() => setSettingsModalOpen(true)}>
                        <MenuDotsIcon />
                    </button>
                </div>
                <div style={{ fontSize: '2.5rem', textAlign: 'center' }}>
                    {greeting}
                </div>
            </div>
            <FocusInputContainer>
                <FocusText style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Today's Focus</FocusText>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <FocusInput
                        type="text"
                        placeholder="Add a new focus item..."
                        value={newFocusItem}
                        onChange={(e) => setNewFocusItem(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleAddFocusItem();
                            }
                        }}
                        style={{ flexGrow: 1 }} // Allow text input to take available space
                    />
                </div>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="focusItems">
                        {(provided) => (
                            <FocusList {...provided.droppableProps} ref={provided.innerRef} style={{ width: focusListWidth, position: 'relative' }}>
                                {focusedItems.map((item, index) => (
                                    <Draggable key={item.id} draggableId={item.id} index={index}>
                                        {(provided) => (
                                            <FocusListItem
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <FocusCheckbox
                                                    type="checkbox"
                                                    onChange={() => {
                                                        const itemIndex = globalTodos.findIndex(todo => todo.id === item.id);
                                                        if (itemIndex > -1) {
                                                            handleGlobalTodoOperations.toggleCompleteAndFocus(itemIndex);
                                                        }
                                                    }}
                                                    checked={item.completed}
                                                />
                                                {item.id === editingItemId ? (
                                                    <>
                                                        <FocusInput
                                                            type="text"
                                                            value={editingItemText}
                                                            onChange={(e) => setEditingItemText(e.target.value)}
                                                            onBlur={() => {
                                                                handleUpdateFocusItem(item.id, editingItemText, editingItemDueDate);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    handleUpdateFocusItem(item.id, editingItemText, editingItemDueDate);
                                                                } else if (e.key === 'Escape') {
                                                                    setEditingItemId(null);
                                                                    setEditingItemDueDate('');
                                                                }
                                                            }}
                                                            autoFocus
                                                        />
                                                        <FocusInput
                                                            type="datetime-local" // Changed from date to datetime-local
                                                            value={editingItemDueDate}
                                                            onChange={(e) => setEditingItemDueDate(e.target.value)}
                                                            style={{ marginLeft: '0.5rem' }}
                                                        />
                                                    </>
                                                ) : (
                                                    // Non-editing mode display
                                                    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}> {/* Wrapper for text, icon, and conditional due date */}
                                                        <span onClick={() => {
                                                            setEditingItemId(item.id);
                                                            setEditingItemText(item.text);
                                                            setEditingItemDueDate(item.dueDate || '');
                                                            setVisibleDueDateItemId(null); // Hide expanded due date
                                                        }} style={{ cursor: 'pointer', flexGrow: 1 }}>
                                                            {item.text}
                                                        </span>
                                                        <DeleteButton
                                                            onClick={() => handleToggleFocus(item)}
                                                            style={{ opacity: 1 }}
                                                            title={item.focused ? "Remove from focused" : "Add to focused"}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <circle cx="12" cy="12" r="10" />
                                                                <circle cx="12" cy="12" r="3" fill={item.focused ? "currentColor" : "none"}/>
                                                            </svg>
                                                        </DeleteButton>
                                                        {item.dueDate && (
                                                            <span
                                                                onClick={() => setVisibleDueDateItemId(visibleDueDateItemId === item.id ? null : item.id)}
                                                                style={{ cursor: 'pointer', marginLeft: '0.5rem', fontSize: '1rem' }}
                                                            >
                                                                📅
                                                            </span>
                                                        )}
                                                        {visibleDueDateItemId === item.id && item.dueDate && (
                                                            <div style={{ fontSize: '0.8em', marginLeft: '1.7rem', marginTop: '0.2rem' }}> {/* Indent slightly more than checkbox */}
                                                                {(() => {
                                                                    const isOverdue = () => {
                                                                        if (!item.dueDate) return false;
                                                                        const dueDateObj = new Date(item.dueDate);
                                                                        const now = new Date();
                                                                        return dueDateObj < now;
                                                                    };
                                                                    const displayDueDate = item.dueDate.replace('T', ' ');
                                                                    return (
                                                                        <span style={{ color: isOverdue() ? 'red' : 'inherit' }}>
                                                                            Due: {displayDueDate}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </FocusListItem>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                <ResizerHandle onMouseDown={handleMouseDown} />
                            </FocusList>
                        )}
                    </Droppable>
                </DragDropContext>
            </FocusInputContainer>
            <ClockSettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setSettingsModalOpen(false)}
                settings={clockSettings}
                onSettingsChange={setClockSettings}
            />
        </ClockContainer>
    );
}

export default DashboardClock;
